import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Briefcase, Star, TrendingUp, Check, X } from "lucide-react";

export default function Dashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const resp = await api.get("/stats");
        setStats(resp.data);
      } catch (e) {
        console.error("Failed to fetch stats", e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: "Total Candidates", value: stats?.total_candidates || 0, icon: Users, color: "text-blue-400", bg: "bg-blue-500/10" },
    { label: "Total Reviews", value: stats?.total_reviews || 0, icon: Star, color: "text-amber-400", bg: "bg-amber-500/10" },
    { label: "Active Roles", value: stats?.total_roles || 0, icon: Briefcase, color: "text-emerald-400", bg: "bg-emerald-500/10" },
    { label: "Acceptance Rate", value: stats?.total_reviews > 0 ? `${Math.round((stats?.accept_count / stats?.total_reviews) * 100)}%` : "N/A", icon: TrendingUp, color: "text-violet-400", bg: "bg-violet-500/10" },
  ];

  return (
    <div className="px-4 py-6 sm:p-6 md:p-8 lg:p-12 space-y-6 lg:space-y-8 animate-fade-in" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white uppercase">Dashboard</h1>
        <p className="text-zinc-500 mt-1 text-sm lg:text-base">Welcome back, <span className="text-zinc-300">{user?.name}</span></p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6" data-testid="stats-grid">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} className="bg-zinc-900/50 border-white/10 hover:border-white/20 transition-colors">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider">{stat.label}</p>
                    <p className="font-mono text-xl sm:text-2xl lg:text-3xl font-bold text-white mt-1 lg:mt-2">{stat.value}</p>
                  </div>
                  <div className={`w-9 h-9 lg:w-12 lg:h-12 rounded-xl ${stat.bg} flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 lg:w-6 lg:h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader className="p-4 lg:p-6 pb-2 lg:pb-3">
            <CardTitle className="font-heading text-base lg:text-lg font-semibold text-white uppercase tracking-tight">Candidates by Role</CardTitle>
          </CardHeader>
          <CardContent className="p-4 lg:p-6 pt-0 space-y-2 lg:space-y-3 max-h-80 overflow-y-auto">
            {stats?.role_stats?.length > 0 ? (
              stats.role_stats.map((role) => (
                <div key={role.role_id} className="flex items-center justify-between p-2.5 lg:p-3 rounded-lg bg-zinc-800/50 border border-white/5">
                  <span className="text-xs lg:text-sm text-zinc-300 truncate mr-2">{role.title}</span>
                  <Badge variant="secondary" className="font-mono text-xs shrink-0">{role.candidate_count}</Badge>
                </div>
              ))
            ) : (
              <p className="text-sm text-zinc-500">No roles created yet.</p>
            )}
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader className="p-4 lg:p-6 pb-2 lg:pb-3">
            <CardTitle className="font-heading text-base lg:text-lg font-semibold text-white uppercase tracking-tight">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="p-4 lg:p-6 pt-0 space-y-2 lg:space-y-3">
            {stats?.recent_activity?.length > 0 ? (
              stats.recent_activity.slice(0, 6).map((activity, i) => (
                <div key={i} className="flex items-center gap-2 lg:gap-3 p-2.5 lg:p-3 rounded-lg bg-zinc-800/50 border border-white/5">
                  <div className={`w-7 h-7 lg:w-8 lg:h-8 rounded-full flex items-center justify-center shrink-0 ${activity.decision === "accept" ? "bg-emerald-500/15" : "bg-red-500/15"}`}>
                    {activity.decision === "accept" ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <X className="w-3.5 h-3.5 text-red-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs lg:text-sm text-zinc-300 truncate">
                      <span className="font-medium">{activity.reviewer_name}</span>{" "}
                      <span className={activity.decision === "accept" ? "text-emerald-400" : "text-red-400"}>{activity.decision === "accept" ? "accepted" : "rejected"}</span>{" "}
                      <span className="text-zinc-400">{activity.candidate_name}</span>
                    </p>
                  </div>
                  <span className="font-mono text-xs text-zinc-600 shrink-0">{activity.score}/5</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-zinc-500">No reviews yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-4 lg:gap-6 p-3 lg:p-4 rounded-xl bg-zinc-900/30 border border-white/5 text-xs lg:text-sm">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 lg:w-3 lg:h-3 rounded-full bg-emerald-500" />
          <span className="text-zinc-400">Accepted: <span className="font-mono text-white">{stats?.accept_count || 0}</span></span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 lg:w-3 lg:h-3 rounded-full bg-red-500" />
          <span className="text-zinc-400">Rejected: <span className="font-mono text-white">{stats?.reject_count || 0}</span></span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 lg:w-3 lg:h-3 rounded-full bg-zinc-500" />
          <span className="text-zinc-400">Pending: <span className="font-mono text-white">{stats?.pending_reviews || 0}</span></span>
        </div>
      </div>
    </div>
  );
}
